import { IconSearch, IconSend, IconUserGroup } from '@medflex/components/stories/Icons';
import { SESSION_TYPES } from '@medflex/utils/constants';
import InputField from '@medflex/components/stories/InputField';
import Modal from '@medflex/components/stories/Modal';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import matchesSearchTerm from '@medflex/utils/matchesSearchTerm';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import joinUmlauts from '@medflex/utils/joinUmlauts';
import ContactList from '@medflex/components/stories/ContactList';
import { useHistory } from 'react-router';
import GroupAvatar from '@medflex/components/stories/GroupAvatar';
import Button from '@medflex/components/stories/Button';
import useDisableRubberBandScrolling from '@medflex/hooks/useDisableRubberBandScrolling';
import sortBy from 'lodash/sortBy';
import Fade, { Container as FadeContainer } from '@medflex/components/stories/Fade';
import createParticipantsString from '@medflex/utils/createParticipantsString';
import SearchHighlight from '@medflex/components/stories/SearchHighlight';
import useIsModal, { MODALS } from '../hooks/useIsModal';
import useModal from '../hooks/useModal';
import useSessionChats from '../hooks/useSessionChats';
import useUserProfile from '../hooks/useUserProfile';

const SessionChatSearchModule = () => {
  const history = useHistory();
  const { userProfile } = useUserProfile();
  const { chats: chatsRaw } = useSessionChats();
  const { t } = useTranslation('messenger');
  const { isModal } = useIsModal();
  const { close, open } = useModal();
  const isWideScreen = useIsWideScreen();
  const [query, setQuery] = useState('');
  const ref = useRef();
  const rubberBandRef = useDisableRubberBandScrolling();
  const [scrollbox, setScrollbox] = useState();

  const handleClose = () => close({ modal: MODALS.sessionChatSearch });

  const handleChatClicked = ({ id }) => {
    history.push(`/chat/${id}`);
  };

  const handleNewGroup = () => {
    open({ modal: 'sessionChatCreate' });
  };

  const getParticipantString = useCallback(createParticipantsString(userProfile.id), [userProfile]);

  const chats = useMemo(
    () =>
      chatsRaw
        .filter(chat => !chat.id.includes('s-i1'))
        .map(chat => ({
          ...chat,
          participantString: getParticipantString(chat.participants),
        })),
    [chatsRaw, getParticipantString],
  );

  const resultSet = useMemo(() => {
    const chatsGroupedByLetter = chats
      .filter(chat => {
        if (chat.type === SESSION_TYPES.TEAM_CHAT && chat.participants.length <= 2) {
          return false;
        }
        return matchesSearchTerm(query, chat.title || chat.participantString);
      })
      .reduce((memo, chat) => {
        const letter = joinUmlauts(
          (chat.title || getParticipantString(chat.participants)).toLowerCase().charAt(0),
        );

        return {
          ...memo,
          [letter]: [...(memo[letter] || []), chat],
        };
      }, {});

    return sortBy(Object.entries(chatsGroupedByLetter), ([key]) => key);
  }, [chats, query, getParticipantString]);

  useEffect(() => {
    if (ref && isModal && isWideScreen) {
      ref.current.focus();
    }
  }, [ref, isModal, isWideScreen]);

  return (
    <Modal
      showCloseButton
      onClickCloseButton={handleClose}
      title={t('SessionChatSearchModule.title')}
      onClickOutside={handleClose}
    >
      <div data-component="SessionChatSearchModule">
        <div>
          <div className="flex mt-2 flex-grow-0 flex-shrink-0 mb-4">
            <InputField
              placeholder={t('ContactsSelect.search.placeholder')}
              className="flex-shrink-0 flex-grow mr-2"
              onChange={event => {
                setQuery(event.target.value);
              }}
              value={query}
              innerRef={ref}
              afterInput={
                <div className="pr-4 flex justiy-center items-center cursor-pointer">
                  <IconSearch size="5" className="fill-current text-slate-500" />
                </div>
              }
            />
          </div>

          <FadeContainer scrollbox={scrollbox}>
            {({ scrolledToEnd }) => (
              <>
                <div
                  className="p-2 overflow-auto max-h-64"
                  ref={node => {
                    setScrollbox(node);
                    rubberBandRef.current = node;
                  }}
                >
                  {resultSet.map(([letter, group]) => (
                    <ContactList key={letter}>
                      <div className="font-semibold">{letter.toUpperCase()}</div>
                      {group.map(chat => (
                        <ContactList.Item
                          enabled
                          avatarSize="8"
                          avatarSrc={<GroupAvatar size="8" />}
                          after={<IconSend className="text-blue-500" />}
                          key={chat.id}
                          onClick={() => handleChatClicked({ id: chat.id })}
                          title={
                            <SearchHighlight
                              text={chat.title || getParticipantString(chat.participants)}
                              toHighlight={query}
                            />
                          }
                          subtitle={chat.title && getParticipantString(chat.participants)}
                        />
                      ))}
                    </ContactList>
                  ))}
                </div>
                <Fade hidden={scrolledToEnd || !resultSet.length} />
              </>
            )}
          </FadeContainer>
        </div>
        <div className="border-t-2 border-black-200 my-6" />
        <div className="p-2">
          <Button kind="link" onClick={handleNewGroup}>
            <IconUserGroup size="text" />
            <span className="ml-2">{t('SessionChatSearchModule.newGroup')}</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SessionChatSearchModule;
